import { useEffect, useMemo, useState } from 'react';
import {
  TrendingUp, TrendingDown, Minus, Zap, Target, Gauge, Activity, ShieldCheck, Lightbulb,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine,
  PieChart, Pie, Cell, Tooltip as RTooltip,
} from 'recharts';
import { API_BASE } from '../../utils/api-base';

interface EnergyForecastProps {
  clientId: string;
  token: string;
}

interface MeterOption {
  id: string;
  name: string;
  tags: { id: string; name: string; unit?: string }[];
}

// Validated categorical palette (see dataviz skill / references/palette.md) — slot 1
// (blue) for actuals, slot 2 (orange) for forecast. This exact pair is documented to
// clear CVD and normal-vision separation in both light and dark surfaces.
const COLOR_ACTUAL = '#2a78d6';
const COLOR_FORECAST = '#eb6834';
const CATEGORICAL = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
// Status palette (fixed, never reused for series identity) — used only for the trend badge.
const STATUS_WARNING = '#fab219';
const STATUS_GOOD = '#0ca30c';
const STATUS_MUTED = '#898781';

export function EnergyForecast({ clientId, token }: EnergyForecastProps) {
  const [meters, setMeters] = useState<MeterOption[]>([]);
  const [meterId, setMeterId] = useState('');
  const [tagId, setTagId] = useState('');
  const [chartData, setChartData] = useState<any[]>([]);
  const [trend, setTrend] = useState<string>('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [unit, setUnit] = useState('');
  const [todayUsage, setTodayUsage] = useState<number | null>(null);
  const [peakToday, setPeakToday] = useState<{ value: number; time: string } | null>(null);
  const [accuracy, setAccuracy] = useState<{ mape: number; rmse: number; confidencePct: number } | null>(null);
  const [distribution, setDistribution] = useState<{ meterId: string; meterName: string; avgValue: number; sharePct: number }[]>([]);

  useEffect(() => {
    if (!clientId || !token) return;
    fetch(`${API_BASE}/api/clients/${clientId}/energy-meters`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((payload) => {
        const list: MeterOption[] = (payload?.meters || []).map((m: any) => ({
          id: m.id,
          name: m.name,
          tags: (m.tags || []).map((t: any) => ({ id: t.id, name: t.name, unit: t.unit })),
        }));
        setMeters(list);
        if (list.length > 0) {
          setMeterId(list[0].id);
          if (list[0].tags.length > 0) setTagId(list[0].tags[0].id);
        }
      })
      .catch(() => {});

    fetch(`${API_BASE}/api/clients/${clientId}/energy-overview`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((payload) => { if (payload?.success) setDistribution(payload.distribution || []); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, token]);

  const selectedMeter = useMemo(() => meters.find((m) => m.id === meterId), [meters, meterId]);
  const selectedTag = useMemo(() => selectedMeter?.tags.find((t) => t.id === tagId), [selectedMeter, tagId]);

  const [rawHistory, setRawHistory] = useState<any[]>([]);
  const [rawForecast, setRawForecast] = useState<any[]>([]);

  useEffect(() => {
    if (!clientId || !token || !meterId || !tagId) return;
    setLoading(true);
    setNote('');
    fetch(`${API_BASE}/api/clients/${clientId}/energy-forecast?meterId=${meterId}&tagId=${tagId}&historyDays=60&forecastDays=7`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((payload) => {
        if (!payload?.success) return;
        setTrend(payload.trend || '');
        setNote(payload.note || '');
        setRawHistory(payload.history || []);
        setRawForecast(payload.forecast || []);
        setTodayUsage(payload.todayUsage ?? null);
        setPeakToday(payload.peakToday ?? null);
        setAccuracy(payload.accuracy ?? null);

        const historyPoints = (payload.history || []).map((h: any) => ({
          date: new Date(h.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
          actual: Number(h.value),
        }));
        const forecastPoints = (payload.forecast || []).map((f: any) => ({
          date: new Date(f.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
          predicted: f.predicted,
          band: [f.low, f.high],
        }));
        // Connect the two lines at the same data point (not a duplicate x) so the
        // forecast visually continues from the last actual reading instead of a gap.
        if (historyPoints.length > 0) {
          const last = historyPoints[historyPoints.length - 1];
          (last as any).predicted = last.actual;
          (last as any).band = [last.actual, last.actual];
        }
        setChartData([...historyPoints, ...forecastPoints]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [clientId, token, meterId, tagId]);

  useEffect(() => {
    setUnit(selectedTag?.unit || '');
  }, [selectedTag]);

  const yesterdayAvg = rawHistory.length > 0 ? rawHistory[rawHistory.length - 1].value : null;
  const tomorrowForecast = rawForecast.length > 0 ? rawForecast[0].predicted : null;
  const forecastAvg = rawForecast.length > 0
    ? rawForecast.reduce((s: number, f: any) => s + f.predicted, 0) / rawForecast.length
    : null;
  const pctChange = yesterdayAvg && forecastAvg ? ((forecastAvg - yesterdayAvg) / yesterdayAvg) * 100 : null;
  const todayVsYesterdayPct = todayUsage != null && yesterdayAvg ? ((todayUsage - yesterdayAvg) / yesterdayAvg) * 100 : null;

  const TrendIcon = trend === 'rising' ? TrendingUp : trend === 'falling' ? TrendingDown : Minus;
  const trendColor = trend === 'rising' ? STATUS_WARNING : trend === 'falling' ? STATUS_GOOD : STATUS_MUTED;
  const trendLabel = trend === 'rising' ? 'Rising' : trend === 'falling' ? 'Falling' : 'Stable';

  const insights = useMemo(() => {
    if (!yesterdayAvg || !forecastAvg) return [] as string[];
    const meterName = selectedMeter?.name || 'this meter';
    const tagName = selectedTag?.name || 'this reading';
    const list: string[] = [];
    if (Math.abs(pctChange || 0) < 3) {
      list.push(`${meterName} — ${tagName} is expected to stay roughly stable over the next 7 days, close to its recent average of ${yesterdayAvg.toFixed(1)}${unit}.`);
    } else {
      const direction = (pctChange || 0) > 0 ? 'rise' : 'fall';
      list.push(`${meterName} — ${tagName} is projected to ${direction} by about ${Math.abs(pctChange || 0).toFixed(1)}% over the next 7 days, following its recent trend and weekly pattern.`);
    }
    if (peakToday) {
      list.push(`Today's peak so far was ${peakToday.value.toFixed(1)}${unit} at ${new Date(peakToday.time).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}.`);
    }
    if (accuracy) {
      list.push(`This model has explained recent daily readings within an average of ${accuracy.mape.toFixed(1)}% (MAPE) over the last 60 days — use that as a sense of how tightly to trust the forecast.`);
    }
    return list;
  }, [yesterdayAvg, forecastAvg, pctChange, selectedMeter, selectedTag, unit, peakToday, accuracy]);

  const transitionLabel = chartData.length > rawForecast.length ? chartData[chartData.length - rawForecast.length - 1]?.date : undefined;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <div className="rounded-md border bg-popover px-3 py-2 shadow-md text-sm">
        <p className="text-muted-foreground text-xs mb-1">{label}</p>
        {payload.map((entry: any, i: number) => {
          if (entry.dataKey === 'band' || entry.value == null) return null;
          const seriesLabel = entry.dataKey === 'actual' ? 'Actual' : 'Forecast';
          const color = entry.dataKey === 'actual' ? COLOR_ACTUAL : COLOR_FORECAST;
          return (
            <div key={i} className="flex items-center gap-2">
              <span className="inline-block w-3 h-0.5 rounded" style={{ backgroundColor: color }} />
              <span className="font-semibold text-foreground">{Number(entry.value).toFixed(2)}{unit}</span>
              <span className="text-muted-foreground text-xs">{seriesLabel}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const statTiles = [
    { label: "Today's Usage", value: todayUsage != null ? `${todayUsage.toFixed(1)}${unit}` : '—', icon: Zap, sub: todayVsYesterdayPct != null ? `${todayVsYesterdayPct >= 0 ? '+' : ''}${todayVsYesterdayPct.toFixed(1)}% vs yesterday` : 'So far today' },
    { label: 'Tomorrow Forecast', value: tomorrowForecast != null ? `${tomorrowForecast.toFixed(1)}${unit}` : '—', icon: Target, sub: 'Next day, projected' },
    { label: 'Peak Today', value: peakToday ? `${peakToday.value.toFixed(1)}${unit}` : '—', icon: Gauge, sub: peakToday ? new Date(peakToday.time).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : 'No data yet' },
    { label: 'Forecast Confidence', value: accuracy ? `${accuracy.confidencePct.toFixed(0)}%` : '—', icon: ShieldCheck, sub: 'From last 60 days fit', color: accuracy && accuracy.confidencePct < 60 ? STATUS_WARNING : STATUS_GOOD },
  ];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Energy Consumption Forecast</h2>
        <p className="text-sm text-muted-foreground">
          A statistical projection (trend + day-of-week pattern) based on your last 60 days of data — a planning aid, not a guarantee.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="w-52">
          <Select value={meterId} onValueChange={(v) => { setMeterId(v); setTagId(''); }}>
            <SelectTrigger><SelectValue placeholder="Select meter" /></SelectTrigger>
            <SelectContent>
              {meters.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-52">
          <Select value={tagId} onValueChange={setTagId}>
            <SelectTrigger><SelectValue placeholder="Select tag" /></SelectTrigger>
            <SelectContent>
              {(selectedMeter?.tags || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Loading forecast...</p>}
      {!loading && note && <p className="text-sm text-muted-foreground italic">{note}</p>}

      {!loading && !note && chartData.length > 0 && (
        <>
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            {statTiles.map((t) => (
              <div key={t.label} className="border rounded-lg p-4">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1 uppercase tracking-wide">
                  <t.icon className="h-3.5 w-3.5" /> {t.label}
                </div>
                <p className="text-2xl font-semibold" style={t.color ? { color: t.color } : undefined}>{t.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{t.sub}</p>
              </div>
            ))}
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <div className="flex items-center gap-2 border rounded-lg px-3 py-2 text-sm" style={{ borderColor: trendColor }}>
                <TrendIcon className="h-4 w-4 shrink-0" style={{ color: trendColor }} />
                <span className="font-medium" style={{ color: trendColor }}>{trendLabel}</span>
                <span className="text-muted-foreground">— {pctChange != null ? `${pctChange >= 0 ? '+' : ''}${pctChange.toFixed(1)}% forecasted vs recent average` : ''}</span>
              </div>

              <div className="h-96 border rounded-lg p-4">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" opacity={0.5} vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#898781' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} width={48} unit={unit} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>} iconType="line" />
                    {transitionLabel && (
                      <ReferenceLine x={transitionLabel} stroke="#c3c2b7" strokeDasharray="4 4" label={{ value: 'Today', fontSize: 10, fill: '#898781', position: 'insideTopRight' }} />
                    )}
                    <Area dataKey="band" name="Forecast range" stroke="none" fill={COLOR_FORECAST} fillOpacity={0.12} connectNulls isAnimationActive={false} />
                    <Line type="monotone" dataKey="actual" name="Actual" stroke={COLOR_ACTUAL} strokeWidth={2} dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: '#fcfcfb' }} connectNulls />
                    <Line type="monotone" dataKey="predicted" name="Forecast" stroke={COLOR_FORECAST} strokeWidth={2} strokeDasharray="6 4" dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: '#fcfcfb' }} connectNulls />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              {accuracy && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="border rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">MAPE</p>
                    <p className="text-lg font-semibold">{accuracy.mape.toFixed(1)}%</p>
                    <p className="text-[11px] text-muted-foreground">Avg. % error</p>
                  </div>
                  <div className="border rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">RMSE</p>
                    <p className="text-lg font-semibold">{accuracy.rmse.toFixed(1)}{unit}</p>
                    <p className="text-[11px] text-muted-foreground">Typical deviation</p>
                  </div>
                  <div className="border rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Confidence</p>
                    <p className="text-lg font-semibold">{accuracy.confidencePct.toFixed(0)}%</p>
                    <p className="text-[11px] text-muted-foreground">Model fit, last 60 days</p>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div className="border rounded-lg p-4">
                <h3 className="font-medium mb-2 flex items-center gap-1.5"><Lightbulb className="h-4 w-4" /> Insights</h3>
                <div className="space-y-2">
                  {insights.map((line, i) => (
                    <p key={i} className="text-xs text-foreground border-l-2 pl-2" style={{ borderColor: CATEGORICAL[i % CATEGORICAL.length] }}>{line}</p>
                  ))}
                </div>
              </div>

              {distribution.length > 0 && (
                <div className="border rounded-lg p-4">
                  <h3 className="font-medium mb-2 flex items-center gap-1.5"><Activity className="h-4 w-4" /> Energy Distribution (30d)</h3>
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={distribution} dataKey="sharePct" nameKey="meterName" innerRadius={40} outerRadius={65} paddingAngle={2}>
                          {distribution.map((_, i) => <Cell key={i} fill={CATEGORICAL[i % CATEGORICAL.length]} stroke="none" />)}
                        </Pie>
                        <RTooltip formatter={(v: any) => `${v}%`} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-1 mt-2">
                    {distribution.map((d, i) => (
                      <div key={d.meterId} className="flex items-center justify-between text-xs">
                        <span className="flex items-center gap-1.5 truncate text-muted-foreground"><span className="w-2 h-2 rounded-full inline-block shrink-0" style={{ backgroundColor: CATEGORICAL[i % CATEGORICAL.length] }} /> {d.meterName}</span>
                        <span className="font-medium shrink-0 ml-2">{d.sharePct}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
